import type { CustomFields } from "@xepp/business-analytics-sdk";
import { getErrorMessage } from "../../utils/error-handler";

/** What a helper may hand in; empty values are dropped before the event leaves. */
export type FieldValue = string | number | boolean | null | undefined;

/** Custom fields are flat by contract, so a missing value is left out rather than sent as null. */
export const withoutEmpty = (fields: Record<string, FieldValue>): CustomFields =>
	Object.fromEntries(
		Object.entries(fields).filter((entry): entry is [string, string | number | boolean] => entry[1] !== null && entry[1] !== undefined && entry[1] !== "")
	);

const ERROR_REASON_MAX_LENGTH = 200;

/**
 * A short, analytics-safe reason for a failed request: the RTK Query error's status
 * for transport failures, otherwise the same message the user was shown.
 */
export const describeError = (error: unknown): string => {
	const rtkError = error as { status?: unknown; data?: unknown } | null | undefined;
	if (rtkError?.status === "FETCH_ERROR") return "network_error";
	if (rtkError?.status === "TIMEOUT_ERROR") return "timeout";
	return getErrorMessage(rtkError?.data).slice(0, ERROR_REASON_MAX_LENGTH);
};

/** A user-entered or stored amount ("10000", "10,000.50", 10000) as a number, or undefined when it is not one. */
export const toAmount = (value: string | number | null | undefined): number | undefined => {
	if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
	if (!value) return undefined;
	const parsed = Number.parseFloat(value.replace(/,/g, ""));
	return Number.isFinite(parsed) ? parsed : undefined;
};
